import React, { Dispatch, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Chip, MenuItem, TextField, Tooltip } from '@mui/material'
import {
  AdCreativeType,
  CreativeImportIssue,
  CreativeImportRowReport,
  isHttpUrl,
  mergeUrlParameters,
  CREATIVE_IMPORT_LIMITS,
  SUPPORTED_CALL_TO_ACTIONS,
} from '@marketing-milk/interfaces'
import { LocalAdPreview, LocalAdPreviewCreative } from '../../../../../components/ad-preview/local'
import {
  CreativeImportAction,
  CreativeImportState,
  EditableRowField,
  RowMediaPreview,
  RowUIState,
} from '../state'
import { toPreviewCreative } from './toPreviewCreative'
import { SlideEditor } from './SlideEditor'
import { CopyPoolEditor } from './CopyPoolEditor'
import { DEFAULT_DYNAMIC_SELECTION, DynamicSelection, clampSelection } from './dynamicSelection'
import { dimensionsLabel } from './aspectLabel'
import { formatTimeRemaining, useTimeRemaining } from '../stagingCountdown'

interface Props {
  state: CreativeImportState
  dispatch: Dispatch<CreativeImportAction>
  onStartOver: () => void
}

// call_to_action / destination_url are edited inline with live validation, so
// their server-computed warning chips would be stale after an edit — hide them.
const LIVE_VALIDATED_FIELDS = new Set(['call_to_action', 'destination_url'])

function IssueChips({
  issues,
  color,
}: {
  issues: CreativeImportIssue[]
  color: 'error' | 'warning'
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {issues.map((issue, i) => (
        <Tooltip key={`${issue.code}-${i}`} title={issue.message}>
          <Chip
            size="small"
            color={color}
            variant="outlined"
            label={issue.code.replace(/_/g, ' ')}
          />
        </Tooltip>
      ))}
    </div>
  )
}

/**
 * One creative in the rail. The second line carries the asset's dimensions rather
 * than its headline: the headline is already on screen twice (preview + field),
 * whereas the format mix is deliberate and visible nowhere else at a glance.
 */
function RailRow({
  row,
  ui,
  previews,
  isActive,
  attention,
  optionRef,
  onSelect,
  onToggle,
  onKeyDown,
}: {
  row: CreativeImportRowReport
  ui?: RowUIState
  previews?: RowMediaPreview[]
  isActive: boolean
  attention: 'error' | 'warning' | null
  optionRef: (el: HTMLDivElement | null) => void
  onSelect: () => void
  onToggle: () => void
  onKeyDown: (e: React.KeyboardEvent) => void
}) {
  const first = previews?.[0]
  // A video's rail still is the canvas-derived poster, not the blob itself.
  const still = first?.kind === 'video' ? first.posterUrl : first?.sourceUrl
  return (
    <div
      ref={optionRef}
      role="option"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={`flex cursor-pointer items-center gap-2 border-b border-l-4 py-2 pr-2 outline-none last:border-b-0 focus:ring-2 focus:ring-inset focus:ring-blue-400 ${
        isActive ? 'border-l-blue-500 bg-blue-50' : 'border-l-transparent hover:bg-gray-50'
      } ${row.selectable ? '' : 'opacity-60'}`}
    >
      <Checkbox
        size="small"
        className="shrink-0"
        checked={ui?.selected ?? false}
        disabled={!row.selectable}
        onClick={e => e.stopPropagation()}
        onChange={onToggle}
        inputProps={{ 'aria-label': `select row ${row.rowNumber}` }}
      />
      {/* object-contain, so the rail shows each asset's real shape — with
          deliberately mixed formats that spread is worth seeing at a glance. */}
      {still ? (
        <img src={still} alt="" className="h-14 w-14 shrink-0 rounded bg-gray-100 object-contain" />
      ) : (
        <div className="h-14 w-14 shrink-0 rounded bg-gray-200" />
      )}
      <div className="min-w-0 flex-1">
        <div className={`truncate text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>
          {ui?.editedName || row.effectiveName}
        </div>
        <div className="truncate text-xs text-gray-500">{mediaLabel(row.format, previews)}</div>
      </div>
      {attention && (
        <Chip
          size="small"
          variant="outlined"
          className="shrink-0"
          color={attention === 'error' ? 'error' : 'warning'}
          label={attention === 'error' ? 'Error' : 'Check'}
        />
      )}
    </div>
  )
}

/** Separates the internal-only name from the copy that actually ships. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="-mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
      {children}
    </div>
  )
}

/** Filtered warnings + the dynamic "incomplete draft" note for a row's detail card. */
function rowStatus(row: CreativeImportRowReport, ui?: RowUIState): React.ReactNode {
  if (!row.selectable) return <IssueChips issues={row.hardErrors} color="error" />
  // Recompute "has copy" from the edited values so the note clears once the AE
  // fills copy in (and appears if they clear it).
  const hasCopy = ui
    ? ui.editedHeadline || ui.editedDescription || ui.editedPrimaryText
    : row.row.headline || row.row.description || row.row.primary_text
  const warnings = row.warnings.filter(w => !w.field || !LIVE_VALIDATED_FIELDS.has(w.field))
  if (hasCopy && warnings.length === 0) return null
  return (
    <div className="flex flex-col gap-1">
      {!hasCopy && (
        <span className="text-gray-500 text-xs">Incomplete draft — copy added in Builder</span>
      )}
      <IssueChips issues={warnings} color="warning" />
    </div>
  )
}

/**
 * Live validation for the destination-URL field. An invalid URL is a warning
 * (the worker falls back to the business website), unless the field itself is
 * over the hard cap. A valid composed URL must also fit the varchar(255) sink.
 */
function urlValidation(
  value: string,
  urlParameters?: string
): { error: boolean; helperText?: string } {
  const trimmed = value.trim()
  if (!trimmed) return { error: false }
  if (trimmed.length > CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength) {
    return {
      error: true,
      helperText: `URL must be ${CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength} characters or fewer.`,
    }
  }
  if (!isHttpUrl(trimmed)) {
    return { error: false, helperText: 'Not a valid URL — the business website will be used.' }
  }
  if (
    mergeUrlParameters(trimmed, urlParameters).length >
    CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength
  ) {
    return {
      error: true,
      helperText: `URL must be ${CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength} characters or fewer (including URL parameters).`,
    }
  }
  return { error: false }
}

/**
 * Character counter that stays muted until the value passes Facebook's recommended
 * length, then turns amber with a truncation note. Replaces the persistent gray
 * sentence with something scannable that doesn't add height until it matters.
 */
function CharCount({
  value,
  recommendedMax,
  overNote = 'Facebook may truncate it',
}: {
  value: string
  recommendedMax: number
  /** Why passing the recommendation matters — not every field's sink is Facebook. */
  overNote?: string
}) {
  const over = value.length > recommendedMax
  return (
    <span className={`block text-right ${over ? 'text-amber-600' : 'text-gray-400'}`}>
      {value.length}/{recommendedMax}
      {over && ` · ${overNote}`}
    </span>
  )
}

/**
 * The rail's second line: what the asset actually IS, which the format mix makes
 * worth seeing at a glance and which appears nowhere else. Dimensions come from
 * the preview extraction (decoded there once, alongside the video poster) rather
 * than from a hook per row — `new Image()` fails silently on a video blob, so
 * decoding here would have needed its own branch anyway.
 */
function mediaLabel(
  format: AdCreativeType,
  previews?: RowMediaPreview[],
  selection?: DynamicSelection
): string {
  const first = previews?.[0]
  const size = first?.width && first?.height ? dimensionsLabel(first.width, first.height) : null
  if (format === AdCreativeType.carousel) {
    return [`Carousel · ${previews?.length ?? 0} cards`, size].filter(Boolean).join(' · ')
  }
  if (format === AdCreativeType.dynamic) {
    const total = previews?.length ?? 0
    // Names the pinned asset, so the click and the card visibly agree.
    return total
      ? `Dynamic · asset ${(selection?.asset ?? 0) + 1} of ${total}`
      : 'Dynamic · 0 assets'
  }
  const kind = format === AdCreativeType.single_video ? 'Video' : null
  return [kind, size].filter(Boolean).join(' · ')
}

/**
 * The ad as it will read, at the asset's true size. Not a fidelity check — the
 * full-fidelity preview lives downstream on the creative itself. This only has to
 * answer "did the right image land next to the right words", so it drops the
 * placeholder page identity and never crops.
 */
function PreviewPanel({
  creative,
  caption,
  note,
}: {
  creative: LocalAdPreviewCreative
  caption?: string | null
  /** A second, explanatory line under the caption — what this preview is and isn't. */
  note?: string | null
}) {
  return (
    // Sticky: the form is taller than the viewport, so scrolling to reach the
    // lower fields would otherwise scroll the ad you're checking them against
    // off the screen.
    <div className="w-full shrink-0 self-start lg:sticky lg:top-4 lg:w-[360px]">
      <LocalAdPreview creative={creative} mediaFit="native" showPageHeader={false} />
      {caption && <p className="mt-2 text-center text-xs text-gray-500">{caption}</p>}
      {note && <p className="mt-1 text-center text-xs italic text-gray-400">{note}</p>}
    </div>
  )
}

/**
 * Whether a row deserves a rail flag, and how loud: blocking = red, warning = amber.
 *
 * Deliberately does NOT flag copy that merely passes Facebook's *recommended*
 * length. AI-written ad copy exceeds it on nearly every row, so including it lit
 * the flag on the whole batch — a warning that fires 6/6 times is decoration, not
 * signal. Soft truncation is already reported twice, in better places: the
 * "See more" cut in the preview and the amber counter under the field. The rail is
 * reserved for what you *can't* see by looking — server warnings and hard errors.
 */
/** Below this the countdown turns urgent rather than informational. */
const TWO_HOURS_MS = 2 * 60 * 60 * 1000

function rowAttention(row: CreativeImportRowReport, blocking: boolean): 'error' | 'warning' | null {
  if (!row.selectable || blocking) return 'error'
  const warnings = row.warnings.filter(w => !w.field || !LIVE_VALIDATED_FIELDS.has(w.field))
  return warnings.length > 0 ? 'warning' : null
}

export const ReviewStep = ({ state, dispatch, onStartOver }: Props) => {
  const report = state.report
  // Ticking, because Review is the surface people leave open for an hour.
  const stagingRemaining = useTimeRemaining(report?.stagingExpiresAt)

  const rows = report?.rows ?? []
  const firstActive = rows.find(r => r.selectable)?.rowNumber ?? rows[0]?.rowNumber ?? 0
  const [activeRowNumber, setActiveRowNumber] = useState(firstActive)
  // Which combination each dynamic row's preview is pinned to. Kept per row so
  // paging away and back keeps your place, and kept in component state on
  // purpose: it must never reach the reducer, the autosave, or the manifest —
  // it chooses what the AE is looking at, not what gets imported.
  const [dynamicSelections, setDynamicSelections] = useState<Record<number, DynamicSelection>>({})
  const optionRefs = useRef<Record<number, HTMLDivElement | null>>({})

  if (!report) return null

  const editField = (rowNumber: number, field: EditableRowField, value: string) =>
    dispatch({ type: 'editField', rowNumber, field, value })

  const selectedCount = rows.filter(r => state.rows[r.rowNumber]?.selected).length
  const selectableCount = rows.filter(r => r.selectable).length

  // A selected row whose edited headline or URL exceeds its sink blocks Continue.
  const isRowBlocking = (row: CreativeImportRowReport): boolean => {
    const ui = state.rows[row.rowNumber]
    return (
      (ui?.selected ?? false) &&
      (ui.editedHeadline.length > CREATIVE_IMPORT_LIMITS.headlineMaxLength ||
        urlValidation(ui.editedDestinationURL, row.row.url_parameters).error)
    )
  }
  const firstBlockingIndex = rows.findIndex(isRowBlocking)
  const hasBlockingError = firstBlockingIndex !== -1

  // Rail positions carrying an error or warning, so the footer can both count them
  // and jump to the first — otherwise an issue mid-batch is only found by paging.
  const attentionIndexes = rows.reduce<number[]>((acc, row, index) => {
    if (rowAttention(row, isRowBlocking(row))) acc.push(index)
    return acc
  }, [])

  const activeIndex = Math.max(
    0,
    rows.findIndex(r => r.rowNumber === activeRowNumber)
  )
  const activeRow = rows[activeIndex]
  const activeUI = activeRow ? state.rows[activeRow.rowNumber] : undefined

  const focusRow = (index: number) => {
    if (index < 0 || index >= rows.length) return
    const rowNumber = rows[index].rowNumber
    setActiveRowNumber(rowNumber)
    optionRefs.current[rowNumber]?.focus()
  }

  // Arrow handling lives on the focused rail option only, so it never swallows
  // arrows used inside the multiline Primary Text field or the CTA select.
  const onOptionKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      focusRow(index + 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      focusRow(index - 1)
    }
  }

  const isLastRow = activeIndex === rows.length - 1

  const editable = activeRow?.selectable ?? false
  const headline = activeUI?.editedHeadline ?? ''
  const headlineTooLong = headline.length > CREATIVE_IMPORT_LIMITS.headlineMaxLength
  const activeUrl = urlValidation(
    activeUI?.editedDestinationURL ?? '',
    activeRow?.row.url_parameters
  )
  const activeStatus = activeRow ? rowStatus(activeRow, activeUI) : null
  // Which copy fields apply is a property of the format: a dynamic ad's pools
  // replace the single primary text / CTA, and a carousel's cards replace the
  // creative-level headline and description.
  const isDynamic = activeRow?.format === AdCreativeType.dynamic
  const isSingle =
    activeRow?.format === AdCreativeType.single_image ||
    activeRow?.format === AdCreativeType.single_video

  const activePreviews = activeRow ? state.previews[activeRow.rowNumber] ?? [] : []
  const activeSelection = clampSelection(
    (activeRow && dynamicSelections[activeRow.rowNumber]) ?? DEFAULT_DYNAMIC_SELECTION,
    activeUI?.editedCopyPools,
    activePreviews.length
  )
  const setActiveSelection = (next: DynamicSelection) =>
    activeRow && setDynamicSelections(prev => ({ ...prev, [activeRow.rowNumber]: next }))

  return (
    <div className="flex flex-col gap-4">
      {/* Reads before the archive warnings: a staged archive that has passed its
          deadline can no longer be confirmed, so redoing Review work on it is
          wasted. Derived from the deadline alone — the reopen itself is allowed. */}
      {stagingRemaining !== null && (
        <Alert
          severity={
            stagingRemaining <= 0 ? 'error' : stagingRemaining <= TWO_HOURS_MS ? 'warning' : 'info'
          }
        >
          {stagingRemaining <= 0
            ? "This upload expired — you'll need to re-upload."
            : `This upload expires in ${formatTimeRemaining(
                stagingRemaining
              )} — confirm before then or you'll need to re-upload.`}
        </Alert>
      )}
      {report.archiveWarnings.map(warning => (
        <Alert key={warning.code} severity="warning">
          {warning.message}
        </Alert>
      ))}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Rail: batch overview + bulk select. Bounded height so it scrolls on its own. */}
        <div className="w-full shrink-0 self-start overflow-hidden rounded-lg border bg-white lg:sticky lg:top-0 lg:w-72">
          <label className="flex items-center gap-2 border-b px-2 py-2 text-sm font-medium">
            <Checkbox
              size="small"
              checked={selectableCount > 0 && selectedCount === selectableCount}
              indeterminate={selectedCount > 0 && selectedCount < selectableCount}
              disabled={selectableCount === 0}
              onChange={() =>
                dispatch({ type: 'setAllRows', selected: selectedCount < selectableCount })
              }
              inputProps={{ 'aria-label': 'select all rows' }}
            />
            Select all
            <span className="ml-auto text-xs font-normal text-mm-neutral-1-600">
              {selectedCount} selected
            </span>
          </label>
          <div
            role="listbox"
            aria-label="Imported creatives"
            className="max-h-[70vh] overflow-y-auto lg:max-h-[calc(100vh-13rem)]"
          >
            {rows.map((row, index) => (
              <RailRow
                key={row.rowNumber}
                row={row}
                ui={state.rows[row.rowNumber]}
                previews={state.previews[row.rowNumber]}
                isActive={row.rowNumber === activeRowNumber}
                attention={rowAttention(row, isRowBlocking(row))}
                optionRef={el => (optionRefs.current[row.rowNumber] = el)}
                onSelect={() => setActiveRowNumber(row.rowNumber)}
                onToggle={() => dispatch({ type: 'toggleRow', rowNumber: row.rowNumber })}
                onKeyDown={e => onOptionKeyDown(e, index)}
              />
            ))}
          </div>
        </div>

        {/* Detail: uncropped preview + edit form for the active creative. */}
        {activeRow && activeUI && (
          <div className="min-w-0 flex-1 rounded-lg border bg-white p-4">
            <div className="flex flex-col gap-6 lg:flex-row">
              <PreviewPanel
                creative={toPreviewCreative(
                  activeUI,
                  activeRow.format,
                  activePreviews,
                  activeSelection
                )}
                caption={mediaLabel(activeRow.format, activePreviews, activeSelection) || null}
                // Dynamic has no one rendering — without this the pin controls
                // read as choosing which variants ship, which they don't.
                note={
                  isDynamic
                    ? 'One possible combination — Facebook mixes all variants at delivery.'
                    : null
                }
              />

              <div className="flex min-w-0 flex-1 flex-col gap-4">
                {/* The pager spans only this column, even though it navigates the
                    whole card: keeping it off the preview's top edge buys the ad
                    itself a pager's worth of above-the-fold height. */}
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-sm text-gray-500">
                    Ad {activeIndex + 1} of {rows.length}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      disabled={activeIndex === 0}
                      onClick={() => focusRow(activeIndex - 1)}
                    >
                      ‹ Prev
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isLastRow}
                      onClick={() => focusRow(activeIndex + 1)}
                    >
                      Next ›
                    </Button>
                  </div>
                </div>
                {!editable && (
                  <Alert severity="error" icon={false}>
                    <div className="mb-1 text-sm">This creative can’t be imported:</div>
                    <IssueChips issues={activeRow.hardErrors} color="error" />
                  </Alert>
                )}
                {editable && !activeUI.selected && (
                  <Alert
                    severity="info"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() =>
                          dispatch({ type: 'toggleRow', rowNumber: activeRow.rowNumber })
                        }
                      >
                        Include
                      </Button>
                    }
                  >
                    This creative is excluded from the import. Select Include to import it with your
                    edits.
                  </Alert>
                )}
                {editable && activeStatus}

                <SectionLabel>Internal</SectionLabel>
                <TextField
                  size="small"
                  fullWidth
                  label="Creative name"
                  helperText="Not shown in the ad."
                  value={activeUI.editedName}
                  disabled={!editable}
                  inputProps={{ maxLength: CREATIVE_IMPORT_LIMITS.creativeNameMaxLength }}
                  onChange={e => editField(activeRow.rowNumber, 'editedName', e.target.value)}
                />
                <SectionLabel>Ad copy</SectionLabel>
                {!isDynamic && (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    label="Primary text"
                    placeholder="Added in Builder"
                    value={activeUI.editedPrimaryText}
                    disabled={!editable}
                    helperText={
                      <CharCount
                        value={activeUI.editedPrimaryText}
                        recommendedMax={CREATIVE_IMPORT_LIMITS.primaryTextRecommendedMax}
                      />
                    }
                    onChange={e =>
                      editField(activeRow.rowNumber, 'editedPrimaryText', e.target.value)
                    }
                  />
                )}
                {/* A carousel has no creative-level headline or description —
                    each card carries its own, edited in the tabs below. */}
                {isSingle && (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      label="Headline"
                      placeholder="Added in Builder"
                      value={headline}
                      disabled={!editable}
                      error={headlineTooLong}
                      inputProps={{ maxLength: CREATIVE_IMPORT_LIMITS.headlineMaxLength }}
                      helperText={
                        headlineTooLong ? (
                          `Headline must be ${CREATIVE_IMPORT_LIMITS.headlineMaxLength} characters or fewer.`
                        ) : (
                          <CharCount
                            value={headline}
                            recommendedMax={CREATIVE_IMPORT_LIMITS.headlineRecommendedMax}
                          />
                        )
                      }
                      onChange={e =>
                        editField(activeRow.rowNumber, 'editedHeadline', e.target.value)
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Description"
                      placeholder="Added in Builder"
                      value={activeUI.editedDescription}
                      disabled={!editable}
                      helperText={
                        <CharCount
                          value={activeUI.editedDescription}
                          recommendedMax={CREATIVE_IMPORT_LIMITS.descriptionRecommendedMax}
                        />
                      }
                      onChange={e =>
                        editField(activeRow.rowNumber, 'editedDescription', e.target.value)
                      }
                    />
                  </>
                )}
                {!isDynamic && (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Call to action"
                    value={activeUI.editedCallToAction}
                    disabled={!editable}
                    onChange={e =>
                      editField(activeRow.rowNumber, 'editedCallToAction', e.target.value)
                    }
                  >
                    {SUPPORTED_CALL_TO_ACTIONS.map(cta => (
                      <MenuItem key={cta} value={cta}>
                        {cta.replace(/_/g, ' ')}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  size="small"
                  fullWidth
                  label="Destination URL"
                  placeholder="Business website"
                  value={activeUI.editedDestinationURL}
                  disabled={!editable}
                  error={activeUrl.error}
                  helperText={activeUrl.helperText}
                  inputProps={{ maxLength: CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength }}
                  onChange={e =>
                    editField(activeRow.rowNumber, 'editedDestinationURL', e.target.value)
                  }
                />
                {activeUI.editedSlides && (
                  <>
                    <SectionLabel>Cards</SectionLabel>
                    <SlideEditor
                      rowNumber={activeRow.rowNumber}
                      slides={activeUI.editedSlides}
                      previews={state.previews[activeRow.rowNumber] ?? []}
                      disabled={!editable}
                      dispatch={dispatch}
                    />
                  </>
                )}
                {activeUI.editedCopyPools && (
                  <>
                    <SectionLabel>Copy pools</SectionLabel>
                    <CopyPoolEditor
                      rowNumber={activeRow.rowNumber}
                      pools={activeUI.editedCopyPools}
                      previews={activePreviews}
                      disabled={!editable}
                      selection={activeSelection}
                      onSelectionChange={setActiveSelection}
                      dispatch={dispatch}
                    />
                  </>
                )}
                {/* S7d. Last, and under its own label: this is the only field on
                    the card that never reaches Facebook — it's the strategy note
                    the client packet reads, so grouping it with ad copy would
                    misrepresent what it does. */}
                <SectionLabel>Strategy</SectionLabel>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  label="Why this ad"
                  placeholder="Optional — appears in the client packet, never in the ad"
                  value={activeUI.editedJustification}
                  disabled={!editable}
                  helperText={
                    <CharCount
                      value={activeUI.editedJustification}
                      recommendedMax={CREATIVE_IMPORT_LIMITS.justificationRecommendedMax}
                      overNote="it will import in full, but reads long in the packet"
                    />
                  }
                  onChange={e =>
                    editField(activeRow.rowNumber, 'editedJustification', e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {selectedCount} of {report.counts.totalRows} rows selected
          {report.counts.blockedRows > 0 && ` · ${report.counts.blockedRows} blocked`}
          {report.counts.orphanMedia > 0 &&
            ` · ${report.counts.orphanMedia} unreferenced files ignored`}
          {attentionIndexes.length > 0 && (
            <>
              {' · '}
              <button
                type="button"
                className="underline"
                onClick={() => focusRow(attentionIndexes[0])}
              >
                {attentionIndexes.length} need a look
              </button>
            </>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {selectedCount === 0 ? (
            <span className="text-sm text-mm-neutral-1-600">
              Select at least one creative to continue.
            </span>
          ) : hasBlockingError ? (
            <Button color="error" onClick={() => focusRow(firstBlockingIndex)}>
              Fix error to continue
            </Button>
          ) : null}
          <Button onClick={onStartOver}>Start Over</Button>
          <Button
            variant="contained"
            disabled={selectedCount === 0 || hasBlockingError}
            onClick={() => dispatch({ type: 'goToOptions' })}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  )
}
